import React, { useEffect, useState } from 'react';

import { LogOut } from 'lucide-react';

import AuthLayout from '../../components/layout/AuthLayout';
import PaymentSummary from '../../components/payment/PaymentSummary';
import { useInvoicePaymentSummary } from '../../hooks/useInvoiceApi';
import { useMemberApi } from '../../hooks/useMemberApi';
import { clearAllTokens } from '../../utils/tokenUtils';

// Reached after login/registration when the member's Active flag is false - the gate that keeps
// unpaid members off the platform's features (Explore bucket-list/courses-played etc), enforced
// server-side by SecuredMiddleware. Gets (or creates) the member's outstanding invoice, then hands
// off to the exact same PaymentSummary/useInvoicePaymentSummary pair the emailed "Pay Now" link
// uses (see Pay.tsx) - one payment UI for both paths, not a second one hand-rolled here.
const PaymentRequired: React.FC = () => {
  const { resumeMembership } = useMemberApi();
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [resumeError, setResumeError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    resumeMembership()
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response?.success && response.data) {
          setInvoiceId(response.data);
        } else {
          setResumeError(
            response?.message ??
              'Could not load your invoice. Please try again.',
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResumeError('Could not load your invoice. Please try again.');
        }
      });

    return () => {
      cancelled = true;
    };
    // Runs once on mount - resumeMembership itself is stable per render but re-invoking it isn't
    // needed once an invoiceId is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
  } = useInvoicePaymentSummary(invoiceId ?? undefined);

  const handleLogout = (): void => {
    clearAllTokens();
    window.location.href = '/login';
  };

  const logoutButton = (
    <button
      type="button"
      onClick={handleLogout}
      className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 text-sm font-semibold text-ink-muted transition-opacity hover:opacity-90"
    >
      <LogOut className="h-4 w-4" />
      Log Out
    </button>
  );

  return (
    <AuthLayout
      title="Membership Payment Required"
      description="Your membership isn't active yet. Complete your payment to unlock the app."
    >
      <div className="mt-6">
        {resumeError && (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {resumeError}
          </p>
        )}

        {!resumeError && (isSummaryLoading || !invoiceId) && <div />}

        {isSummaryError && (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            We could not find that invoice. Please contact support.
          </p>
        )}

        {summary && <PaymentSummary summary={summary} />}

        {logoutButton}
      </div>
    </AuthLayout>
  );
};

export default PaymentRequired;
